import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Sparkles, Home, Layers, Star, Loader2, Tag, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { validateCoupon } from "@/services/couponService";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { isNative } from "@/lib/platform";
import { getOfferings, purchasePackage, restorePurchases } from "@/lib/purchases";

const plans = [
    {
        id: 'free',
        name: 'Free',
        priceMonthly: 0,
        priceAnnual: 0,
        houses: '1 House',
        features: ['1 Property', 'Basic Home Records (5 items)', '1 Service Log per Appliance'],
        icon: Home,
        color: 'text-slate-500',
        bgColor: 'bg-slate-500/10'
    },
    {
        id: 'premium_1',
        name: 'Essential',
        priceMonthly: 49,
        priceAnnual: 490,
        houses: '1 House',
        features: ['1 Property', 'Unlimited Home Records', 'Full Service History'],
        icon: Star,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        popular: true
    },
    {
        id: 'premium_3',
        name: 'Investor',
        priceMonthly: 129,
        priceAnnual: 1290,
        houses: 'Up to 3 Houses',
        features: ['3 Properties', 'Everything in Essential'],
        icon: Layers,
        color: 'text-purple-500',
        bgColor: 'bg-purple-500/10'
    },
    {
        id: 'premium_5',
        name: 'Professional',
        priceMonthly: 199,
        priceAnnual: 1990,
        houses: 'Up to 5 Houses',
        features: ['5 Properties', 'Property Manager Dashboard'],
        icon: Layers,
        color: 'text-blue-600',
        bgColor: 'bg-blue-600/10'
    },
    {
        id: 'enterprise',
        name: 'Estate',
        priceMonthly: null,
        priceAnnual: null,
        houses: 'Unlimited Houses',
        features: ['Unlimited Properties', 'Team Access'],
        icon: Sparkles,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10'
    }
];

import { useSubscription } from "@/hooks/useSubscription";
import { getPrice } from "@/lib/platform";

const UpgradePlanSheet = ({ open, onOpenChange, currentPlan }) => {
    const { isTrialActive, hasUsedTrial, plan } = useSubscription();

    const [isActivatingTrial, setIsActivatingTrial] = useState(false);
    const [billingCycle, setBillingCycle] = useState("annual");
    const [packages, setPackages] = useState([]);
    const [loadingOfferings, setLoadingOfferings] = useState(false);

    useEffect(() => {
        if (open && isNative()) {
            const fetchOfferings = async () => {
                setLoadingOfferings(true);
                try {
                    const pkgs = await getOfferings();
                    setPackages(pkgs || []);
                } catch (err) {
                    console.error("Failed to load offerings:", err);
                } finally {
                    setLoadingOfferings(false);
                }
            };
            fetchOfferings();
        }
    }, [open]);

    const handleStartTrial = async () => {
        setIsActivatingTrial(true);
        try {
            const { data, error } = await supabase.rpc('start_7day_trial');
            if (error) throw error;
            if (data?.success) {
                toast.success("Trial Activated! Enjoy premium for 7 days.");
                onOpenChange(false);
                if (typeof window !== 'undefined') {
                    const event = new CustomEvent('refetch-profile');
                    window.dispatchEvent(event);
                }
            } else {
                toast.error(data?.message || "Failed to start trial.");
            }
        } catch (error) {
            toast.error(error.message || "Something went wrong.");
        } finally {
            setIsActivatingTrial(false);
        }
    };

    const [couponCode, setCouponCode] = useState("");
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [activeCoupon, setActiveCoupon] = useState(null);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setValidatingCoupon(true);
        try {
            const coupon = await validateCoupon(couponCode);
            setActiveCoupon(coupon);
            toast.success(`Applied ${coupon.name}!`);
        } catch (error) {
            toast.error(error.message || "Invalid coupon code");
            setActiveCoupon(null);
        } finally {
            setValidatingCoupon(false);
        }
    };

    const removeCoupon = () => {
        setActiveCoupon(null);
        setCouponCode("");
        toast.info("Coupon removed");
    };

    const calculatePrice = (planBasePrice) => {
        // First get the price for the current platform (Markup if native)
        const platformPrice = getPrice(planBasePrice);

        // Then apply coupons if any (usually web only)
        if (!activeCoupon) return platformPrice;
        if (platformPrice === 0) return 0;

        if (activeCoupon.type === 'percent') {
            const discounted = platformPrice * (1 - activeCoupon.value / 100);
            return Math.round(discounted);
        }
        return platformPrice;
    };

    const { user } = useAuth();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleRestore = async () => {
        setIsProcessing(true);
        const loadingToastId = toast.loading("Restoring purchases...");
        try {
            const customerInfo = await restorePurchases();
            const activeEntitlements = customerInfo?.entitlements?.active || {};
            const activeKeys = Object.keys(activeEntitlements);

            if (customerInfo && activeKeys.length > 0) {
                let activePlan = 'premium_1';
                let maxProps = 1;
                if (activeKeys.some(k => k.toLowerCase().includes('investor') || k.toLowerCase().includes('premium_3'))) {
                    activePlan = 'premium_3';
                    maxProps = 3;
                } else if (activeKeys.some(k => k.toLowerCase().includes('professional') || k.toLowerCase().includes('premium_5'))) {
                    activePlan = 'premium_5';
                    maxProps = 5;
                }

                if (user?.id) {
                    await supabase
                        .from('profiles')
                        .update({
                            subscription_plan: activePlan,
                            max_properties: maxProps,
                            has_premium_features: true
                        })
                        .eq('id', user.id);
                }

                toast.dismiss(loadingToastId);
                toast.success("Purchases restored successfully!");
                onOpenChange(false);
                if (typeof window !== 'undefined') {
                    window.location.reload();
                }
            } else {
                toast.dismiss(loadingToastId);
                toast.info("No active subscriptions found to restore.");
            }
        } catch (err) {
            toast.dismiss(loadingToastId);
            toast.error(err.message || "Failed to restore purchases.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Secure API Initialization
    const handleSelectPlan = async (selectedPlan) => {
        if (selectedPlan.id === 'enterprise') {
            window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Estate Plan Inquiry";
            return;
        }

        setIsProcessing(true);
        let loadingToastId;
        try {
            const planPrice = billingCycle === 'annual' ? selectedPlan.priceAnnual : selectedPlan.priceMonthly;
            const finalPrice = calculatePrice(planPrice);

            // 1. If plan is Free or coupon discount makes it 0, activate directly
            if (finalPrice <= 0) {
                const { data: grantData, error: grantError } = await supabase.rpc('grant_free_subscription', {
                    plan_id: selectedPlan.id,
                    coupon_id: activeCoupon ? activeCoupon.id : null
                });
                if (grantError) throw grantError;
                if (!grantData?.success) throw new Error(grantData?.message || "Upgrade failed");

                toast.success(`Welcome to ${selectedPlan.name}! Plan activated.`);
                onOpenChange(false);
                if (typeof window !== 'undefined') {
                    window.location.reload();
                }
                return;
            }

            // 2. Paid Plan: Native purchase using RevenueCat
            if (!isNative()) {
                toast.error("Subscriptions can only be purchased through the mobile app. Please open HomeEzi on your phone to upgrade.");
                return;
            }

            loadingToastId = toast.loading("Opening secure checkout...");

            const planKey = selectedPlan.id === 'premium_1' ? 'essential' : selectedPlan.id === 'premium_3' ? 'investor' : 'professional';
            const targetProductId = `com.homeezi.app.${planKey}.${billingCycle}`;
            
            const matchPkg = packages.find(p => 
                p.product?.identifier === targetProductId || 
                p.identifier === targetProductId ||
                (p.product?.identifier?.toLowerCase().includes(planKey) && p.product?.identifier?.toLowerCase().includes(billingCycle)) ||
                (p.identifier?.toLowerCase().includes(planKey) && p.identifier?.toLowerCase().includes(billingCycle))
            ) || packages.find(p => p.product?.identifier?.toLowerCase().includes(planKey)) || packages[0];

            if (!matchPkg) {
                throw new Error("This subscription plan is currently unavailable in the store. Please try again later.");
            }

            const customerInfo = await purchasePackage(matchPkg);
            
            if (customerInfo) {
                if (user?.id) {
                    const maxProps = selectedPlan.id === 'premium_1' ? 1 : selectedPlan.id === 'premium_3' ? 3 : 5;
                    try {
                        await supabase
                            .from('profiles')
                            .update({
                                subscription_plan: selectedPlan.id,
                                max_properties: maxProps,
                                has_premium_features: true
                            })
                            .eq('id', user.id);
                    } catch (e) {
                        console.error("Failed to update profile subscription:", e);
                    }
                }

                toast.dismiss(loadingToastId);
                toast.success(`Welcome to ${selectedPlan.name}! Plan activated.`);
                onOpenChange(false);
                if (typeof window !== 'undefined') {
                    window.location.reload();
                }
            } else {
                // User cancelled the native purchase sheet
                toast.dismiss(loadingToastId);
            }

        } catch (error) {
            console.error("Upgrade Error:", error);
            if (loadingToastId) toast.dismiss(loadingToastId);
            toast.error(error.message || "Something went wrong. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" className="max-h-[95vh] h-[95vh] rounded-t-[32px] overflow-y-auto pb-10">
                <SheetHeader className="pb-6">
                    <SheetTitle className="text-2xl font-bold flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-amber-500" />
                        Upgrade Your HomeEzi
                    </SheetTitle>
                    <SheetDescription>
                        Select a plan that fits your property portfolio.
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-6">
                    {/* Free Trial Promotion Card */}
                    {plan === 'free' && !hasUsedTrial && (
                        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-purple-500/10 p-5 rounded-2xl border-2 border-primary/20 shadow-lg relative overflow-hidden group">
                            <div className="absolute -right-8 -top-8 w-24 h-24 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />
                            <div className="relative flex items-start gap-4">
                                <div className="p-3 bg-primary/10 rounded-xl shrink-0">
                                    <Sparkles className="w-6 h-6 text-primary animate-pulse" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                        Free Premium Trial
                                        <span className="text-[10px] bg-primary text-white px-2 py-0.5 rounded-full uppercase">One-Time Offer</span>
                                    </h3>
                                    <p className="text-sm text-muted-foreground mt-1 mb-4">
                                        Test drive all premium features for 7 days. Add up to 2 houses and unlimited home records.
                                    </p>
                                    <ul className="space-y-2 mb-5">
                                        <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Check className="w-3 h-3 text-green-500" /> Unlock 2 Houses
                                        </li>
                                        <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Check className="w-3 h-3 text-green-500" /> Unlimited Home Records
                                        </li>
                                        <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Check className="w-3 h-3 text-green-500" /> Full Service History
                                        </li>
                                    </ul>
                                    <Button
                                        onClick={handleStartTrial}
                                        disabled={isActivatingTrial}
                                        className="w-full rounded-xl font-bold bg-primary shadow-xl shadow-primary/20"
                                    >
                                        {isActivatingTrial ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start My 7-Day Trial"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active Trial Indicator */}
                    {isTrialActive && (
                        <div className="bg-amber-500/10 p-4 rounded-xl border border-amber-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Sparkles className="w-5 h-5 text-amber-500" />
                                <div>
                                    <p className="text-sm font-bold text-amber-900 dark:text-amber-200">Trial Active</p>
                                    <p className="text-[10px] text-amber-800/70 dark:text-amber-100/70">Expires in 7 days</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Coupon Section */}
                    <div className="bg-muted/50 p-4 rounded-xl border border-border/50">
                        <div className="flex items-center gap-2 mb-3">
                            <Tag className="w-4 h-4 text-primary" />
                            <h3 className="font-semibold text-sm">Have a promo code?</h3>
                        </div>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Input
                                    placeholder="Enter code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    disabled={!!activeCoupon}
                                    className="bg-background"
                                    autoFocus={false}
                                />
                                {activeCoupon && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <Check className="w-4 h-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {activeCoupon ? (
                                <Button variant="ghost" size="icon" onClick={removeCoupon} className="shrink-0 text-muted-foreground hover:text-destructive">
                                    <X className="w-4 h-4" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode.trim() || validatingCoupon}
                                    className="shrink-0"
                                >
                                    {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                                </Button>
                            )}
                        </div>
                        {activeCoupon && (
                            <p className="text-xs text-green-600 mt-2 font-medium flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                {activeCoupon.description} applied
                            </p>
                        )}
                    </div>

                    {/* Billing Toggle */}
                    <div className="flex justify-center my-6">
                        <div className="bg-muted/50 p-1 rounded-full inline-flex items-center relative border border-border/50">
                            <button
                                onClick={() => setBillingCycle('monthly')}
                                className={cn(
                                    "px-6 py-2 rounded-full text-sm font-bold transition-all z-10",
                                    billingCycle === 'monthly' ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingCycle('annual')}
                                className={cn(
                                    "px-6 py-2 rounded-full text-sm font-bold transition-all z-10 flex items-center gap-2",
                                    billingCycle === 'annual' ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                Annually
                                <span className={cn(
                                    "text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full transition-colors",
                                    billingCycle === 'annual' ? "bg-primary text-white" : "bg-primary/20 text-primary"
                                )}>
                                    2 Mos Free
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {plans.map((plan) => {
                            const Icon = plan.icon;
                            const isCurrent = currentPlan === plan.id;

                            // Calculate the price for the current platform (including any markups)
                            const planPrice = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
                            
                            let basePlatformPrice = 0;
                            let finalPrice = 0;
                            let hasDiscount = false;

                            if (planPrice !== null) {
                                basePlatformPrice = getPrice(planPrice);
                                finalPrice = calculatePrice(planPrice);
                                hasDiscount = activeCoupon && planPrice > 0 && finalPrice < basePlatformPrice;
                            }

                            return (
                                <div
                                    key={plan.id}
                                    className={cn(
                                        "relative p-5 rounded-2xl border-2 transition-all duration-300",
                                        isCurrent ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                                        plan.popular && !isCurrent && "border-blue-500/30"
                                    )}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-3 right-4 bg-blue-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className="flex items-start gap-4">
                                        <div className={cn("p-3 rounded-xl shrink-0", plan.bgColor)}>
                                            <Icon className={cn("w-6 h-6", plan.color)} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <h3 className="font-bold text-lg">{plan.name}</h3>
                                                <div className="text-right">
                                                    {planPrice === null ? (
                                                        <span className="font-bold text-xl">Contact Us</span>
                                                    ) : hasDiscount ? (
                                                        <>
                                                            <span className="text-sm font-medium text-muted-foreground line-through mr-2">R{basePlatformPrice}</span>
                                                            <span className="font-bold text-xl text-green-600">R{finalPrice}<span className="text-sm font-normal text-muted-foreground/80">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span></span>
                                                        </>
                                                    ) : (
                                                        <span className="font-bold text-xl">R{basePlatformPrice}<span className="text-sm font-normal text-muted-foreground">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span></span>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-sm text-primary font-medium mb-3">{plan.houses}</p>

                                            <ul className="space-y-2">
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                        <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>

                                            <Button
                                                onClick={() => handleSelectPlan(plan)}
                                                className={cn(
                                                    "w-full mt-5 rounded-xl py-6 font-bold",
                                                    isCurrent ? "bg-muted text-muted-foreground pointer-events-none" : ""
                                                )}
                                                variant={isCurrent ? "outline" : (plan.popular ? "default" : "secondary")}
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : isCurrent ? (
                                                    "Current Plan"
                                                ) : plan.id === 'enterprise' ? (
                                                    "Contact Sales"
                                                ) : hasDiscount && finalPrice === 0 ? (
                                                    "Claim for Free"
                                                ) : (
                                                    "Choose " + plan.name
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {isNative() && (
                        <div className="flex justify-center mt-6">
                            <Button 
                                variant="link" 
                                size="sm" 
                                className="text-xs text-muted-foreground"
                                onClick={handleRestore}
                                disabled={isProcessing}
                            >
                                Already purchased? Restore Purchases
                            </Button>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default UpgradePlanSheet;
